import { Controller } from "@hotwired/stimulus"

// Narrows the task form's status field (Tasks::StatusFieldComponent, hidden #task_column_id) to
// the columns the selected site offers — the request board while the site is Angefragt/Beauftragt,
// the normal board once Aktiv — on load and whenever the site changes. sites#get_columns renders
// the field's list with its selection; the status picker takes the selection from it.
//
// Selection is kept by column id, never by name, which two columns can share ("Neu"). On load,
// the task's column stays in the list even where the site's board lacks it (`keep`), so opening
// and saving a task never moves it; after a site change the new board's first Offen status is
// taken instead. An unfinished task closes through Abschließen, not the form, so its list leaves
// the closing statuses out (excludeClosed).
//
// A new task (typeDefault) starts in its type's Start-Status (BAUTASK-480): changing the type or
// the site asks the server for that status (Tasks::DefaultColumn, which also maps it onto the
// request board) — until the user picks a status, which then stays theirs. The server sets
// typeDefault only where it picked the status itself (tasks#new without a column), so a status
// passed in or re-rendered after a failed create is never replaced.
export default class extends Controller {
  static values = { excludeClosed: Boolean, typeDefault: Boolean }

  connect() {
    this.statusPicked = false
    this.markPicked = () => (this.statusPicked = true)
    // Only a pick in the status picker fires change; a list swapped in here does not.
    this.statusInput = document.getElementById("task_column_id")
    this.statusInput?.addEventListener("change", this.markPicked)
    this.load({ keepMissing: true })
  }

  disconnect() {
    this.statusInput?.removeEventListener("change", this.markPicked)
  }

  siteChanged() {
    this.load({ keepMissing: false })
  }

  typeChanged() {
    if (this.typeDefaultValue && !this.statusPicked) this.load({ keepMissing: false })
  }

  async load({ keepMissing }) {
    const siteId = document.getElementById("task_site_id")?.value
    const input = document.getElementById("task_column_id")
    const list = input
      ?.closest("[data-controller~='status-picker']")
      ?.querySelector("[data-status-picker-target~='list']")
    if (!siteId || !list) return

    const query = new URLSearchParams()
    if (this.excludeClosedValue) query.set("exclude_closed", "1")
    if (input.value) query.set("selected", input.value)
    if (input.value && keepMissing) query.set("keep", input.value)
    const typeId = document.getElementById("task_task_type_id")?.value
    if (this.typeDefaultValue && !this.statusPicked && !keepMissing && typeId)
      query.set("task_type_id", typeId)

    // A later site change supersedes a response still on its way.
    const request = (this.request = (this.request || 0) + 1)
    const requested = input.value
    try {
      const response = await fetch(`/sites/${siteId}/get_columns?${query}`, {
        headers: { Accept: "text/html" },
      })
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
      const html = await response.text()
      if (request === this.request) list.replaceChildren(this.parse(html, input.value, requested))
    } catch (error) {
      console.error("Failed to fetch columns:", error)
    }
  }

  // A status picked while the list was on its way wins over the selection the server rendered,
  // as long as the new list has it.
  parse(html, current, requested) {
    const template = document.createElement("template")
    template.innerHTML = html
    const options = [...template.content.querySelectorAll("[role='option']")]
    const picked =
      current !== requested && options.find((option) => option.dataset.columnId === current)
    if (picked)
      options.forEach((option) => option.setAttribute("aria-selected", String(option === picked)))
    return template.content
  }
}
